import {graphql} from 'react-relay';
import {buildTicketFilters, type TicketFiltersInput} from '../../tickets/hooks/useTicketListQuery';
import type {DashboardFilterState, DateRange} from './useDashboardFilterPanel';
import type {DashboardDateField} from './useDashboardFilters';

/**
 * Rows per page, and the increment each "Load more" adds. Shared by the route's
 * query variables and the pagination hook so the first page and every later one
 * are the same size.
 */
export const DASHBOARD_TICKET_PAGE_SIZE = 50;

export const DashboardTicketsQuery = graphql`
	query useDashboardTicketsQueryQuery($first: Int, $after: String, $filters: TicketFilters) {
		viewer {
			actualPersonId
			...useDashboardTicketsQuery_tickets @arguments(first: $first, after: $after, filters: $filters)
		}
	}
`;

export const DashboardTicketsPaginationFragment = graphql`
	fragment useDashboardTicketsQuery_tickets on Viewer
	@refetchable(queryName: "useDashboardTicketsQueryPaginationQuery")
	@argumentDefinitions(first: {type: "Int", defaultValue: 50}, after: {type: "String"}, filters: {type: "TicketFilters"}) {
		tickets(first: $first, after: $after, filters: $filters) @connection(key: "DashboardTickets_tickets") {
			edges {
				cursor
				node {
					id
					subject
					status
					priority
					channel
					slug
					requesterId
					assigneeId
					createdAt
					resolvedAt
					lastActivityAt
					resolutionTimeSeconds
					firstResponseTimeSeconds
					requester {
						id
						name
						email
					}
					assignee {
						fullName
					}
					labels {
						id
						name
						color
					}
					sla {
						breached
						elapsedSeconds
						thresholdSeconds
						paused
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;

/** GraphQL `TicketFilters` input keys for each date field's range pair. */
const DATE_FIELD_RANGE_KEYS: Record<DashboardDateField, {after: string; before: string}> = {
	created_at: {after: 'createdAfter', before: 'createdBefore'},
	last_activity_at: {after: 'lastActivityAfter', before: 'lastActivityBefore'},
	resolved_at: {after: 'resolvedAfter', before: 'resolvedBefore'},
};

/**
 * Intersect a range already written to `target` under `keys` with a further
 * range: the later `from` and the earlier `to` win, so a panel range applied
 * over the same field as the toolbar narrows rather than overwrites it.
 * A missing endpoint on either side does not extend the other's bound.
 */
function narrowRange(target: Record<string, unknown>, keys: {after: string; before: string}, from: string, to: string): void {
	if (from) {
		const existing = target[keys.after];
		target[keys.after] = typeof existing === 'string' && existing > from ? existing : from;
	}
	if (to) {
		const existing = target[keys.before];
		target[keys.before] = typeof existing === 'string' && existing < to ? existing : to;
	}
}

/** Write a panel range into `target` under the given date field's keys. */
function applyPanelRange(target: Record<string, unknown>, dateField: DashboardDateField, range: DateRange): void {
	if (!range.from && !range.to) return;
	narrowRange(target, DATE_FIELD_RANGE_KEYS[dateField], range.from, range.to);
}

/**
 * Maps the dashboard's filter-panel state and toolbar date range into a
 * GraphQL-compatible `TicketFilters` object.
 *
 * The toolbar's date field selects which range pair its from/to write to. The
 * filter panel's three independent ranges (last-activity, resolved, first-
 * response) each write to their own pair, and compose with the toolbar range:
 * when the panel targets the same field the toolbar is on, the intersection
 * (later `from`, earlier `to`) wins so the panel narrows rather than
 * overwrites.
 *
 * `groupBy` is deliberately not included — changing the grouping re-lays-out
 * loaded rows client-side and must never cause a refetch.
 */
export function buildDashboardTicketFilters(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField
): Record<string, unknown> | undefined {
	const filtersInput: TicketFiltersInput = {
		status: applied.status,
		priority: applied.priority,
		sla_status: applied.slaState,
		assignee_id: applied.assignee,
		label_id: applied.tags,
		requester_id: applied.requester,
		created_after: '',
		created_before: '',
		updated_after: '',
		updated_before: '',
	};

	const base = buildTicketFilters(filtersInput, undefined, undefined, undefined);
	const result: Record<string, unknown> = base ? {...base} : {};

	if (from || to) {
		narrowRange(result, DATE_FIELD_RANGE_KEYS[dateField], from, to);
	}

	applyPanelRange(result, 'last_activity_at', applied.lastActivityRange);
	applyPanelRange(result, 'resolved_at', applied.resolvedDateRange);

	if (applied.firstResponseRange.from) result.firstResponseAfter = applied.firstResponseRange.from;
	if (applied.firstResponseRange.to) result.firstResponseBefore = applied.firstResponseRange.to;

	if (applied.channel.length > 0) {
		result.channel = applied.channel;
	}

	return Object.keys(result).length > 0 ? result : undefined;
}
